import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import { Formik, Form } from 'formik';

import { Box, Grid, Paper, Skeleton, Typography } from '@mui/material';

import { Uuid } from '../../../../utils/uuid';

import { convertFileToBase64, normalizeError } from 'app/utils';

import { actions as AppActions } from '../../../App/Ducks/App.duck';
import { MESSAGE_SEVERITY_ERROR } from '../../../../common/constants';

import ownerService from '../../Services/OwnerService';

import { useBreadcrumb } from 'app/components/Breadcrumbs';

import CreateOwnerForm from '../../Forms/CreateOwnerForm';

import { ButtonStyled, PaperInfoStyled } from './OwnerAddPage.styles';

const OwnerAddPage = () => {
  // uses
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const intl = useIntl();

  // data
  const [ownersTotalCount, setOwnersTotalCount] = useState({});

  const userId = useSelector(state => state.AppReducer.user.userId);

  const formikInitialValues = useMemo(
    () => ({
      ownerId: Uuid.newV4(),
      ownerName: '',
      ownerEmail: '',
      ownerPhone: '',
      contactName: '',
      contactEmail: '',
      contactPhone: '',
      address: '',
      zipCode: '',
      city: '',
      instance: '',
      kvkNumber: '',
      parentId: null,
      subOwners: [],
      logo: '',
    }),
    [],
  );

  const formikValidationSchema = useMemo(
    () =>
      Yup.object().shape({
        ownerName: Yup.string()
          .trim()
          .strict()
          .max(255)
          .required(intl.formatMessage({ id: 'owners.form.ownerName.required' })),
        ownerEmail: Yup.string().trim().strict().max(255).email(),
        ownerPhone: Yup.string().trim().strict().max(255),
        contactName: Yup.string()
          .trim()
          .strict()
          .max(255)
          .required(intl.formatMessage({ id: 'owners.form.contactName.required' })),
        contactEmail: Yup.string()
          .trim()
          .strict()
          .max(255)
          .required(intl.formatMessage({ id: 'owners.form.contactEmail.required' }))
          .email(),
        contactPhone: Yup.string().trim().strict().max(255),
        address: Yup.string()
          .trim()
          .strict()
          .required(intl.formatMessage({ id: 'owners.form.address.required' }))
          .max(255),
        zipCode: Yup.string().trim().strict().max(255),
        city: Yup.string().trim().strict().max(255),
        instance: Yup.string(),
        kvkNumber: Yup.string().when('instance', {
          is: instance => instance && instance.trim().length > 0,
          then: schema =>
            schema
              .trim()
              .strict()
              .required(intl.formatMessage({ id: 'owners.form.kvkNumber.required' }))
              .test('is-number', intl.formatMessage({ id: 'form.validNumber' }), value => !isNaN(Number(value))),
          otherwise: schema =>
            schema
              .trim()
              .strict()
              .test('is-number', intl.formatMessage({ id: 'form.validNumber' }), function (value) {
                if (!value) return true;
                return !isNaN(Number(value));
              }),
        }),
      }),
    [intl],
  );

  const saveOwner = useCallback(async values => {
    const valuesToSubmit = { ...values };

    if (values.logo && typeof values.logo !== 'string') {
      valuesToSubmit.logo = await convertFileToBase64(values.logo);
    }

    return ownerService.createOwner(valuesToSubmit);
  }, []);

  const loadTotalCount = useCallback(async () => {
    try {
      const response = await ownerService.getOwnersTotalCount(userId);
      setOwnersTotalCount(response.data);
    } catch (error) {
      const severity = MESSAGE_SEVERITY_ERROR;
      const details = {
        method: 'getOwnersTotalCount',
        severity,
      };
      dispatch(AppActions.displayMessage(details));
    }
  }, [dispatch, userId]);

  // effects
  useEffect(() => {
    loadTotalCount();
  }, [loadTotalCount]);

  // events
  const handleCancel = useCallback(() => {
    navigate('../');
  }, [navigate]);

  const handleCreateOwner = useCallback(
    async values => {
      try {
        await saveOwner(values);
        navigate('../');
      } catch (err) {
        const errors = normalizeError(err);
        const severity = MESSAGE_SEVERITY_ERROR;
        const details = {
          method: 'createOwner',
          severity,
          message: errors.message,
        };
        dispatch(AppActions.displayMessage(details));
      }
    },
    [dispatch, navigate, saveOwner],
  );

  const title = useMemo(() => {
    return (
      <div>
        <Typography variant="h5">
          <FormattedMessage id="owners.add" />
        </Typography>
        <Typography variant="subtitle2" sx={{ textTransform: 'uppercase' }}>
          {typeof ownersTotalCount?.total !== 'undefined' ? (
            <FormattedMessage id="owners.totalCount" values={{ totalCount: ownersTotalCount.total }} />
          ) : (
            <Skeleton width={200} />
          )}
        </Typography>
      </div>
    );
  }, [ownersTotalCount]);

  useBreadcrumb('owners.add', {
    hideTrail: true,
    titleElement: title,
  });

  return (
    <Paper elevation={0} square>
      <Formik
        initialValues={formikInitialValues}
        validationSchema={formikValidationSchema}
        onSubmit={handleCreateOwner}
      >
        {() => (
          <Form>
            <Grid container spacing={3} direction={'column'} xs={6}>
              {/* owner form */}
              <Grid item xs={12}>
                <PaperInfoStyled>
                  <CreateOwnerForm onLogoChange={() => {}} />
                </PaperInfoStyled>
              </Grid>

              {/* buttons */}
              <Grid item xs={12}>
                <Box textAlign="right">
                  <ButtonStyled onClick={handleCancel} variant="outlined">
                    <FormattedMessage id="button.cancel" />
                  </ButtonStyled>

                  <ButtonStyled type="submit" variant="contained">
                    <FormattedMessage id="owners.add.createNewOwner" />
                  </ButtonStyled>
                </Box>
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    </Paper>
  );
};

export default OwnerAddPage;
